import React, { useMemo } from "react";
import SelectPicker from "./SelectPicker.js";

export const locationLabel = (l) =>
    l ? `${l.location_name}${l.location_code ? ` (${l.location_code})` : ""}` : "";

/**
 * Single-location picker on DatalistSelect (searchable; bottom sheet on phones).
 *
 *   <LocationPicker locations={locs} value={locationId} onChange={(id) => …} />
 *
 * `value` (the location id) is the source of truth; the typed text is local and
 * only a real pick calls onChange(id) — a string id, or "" for the empty entry.
 * The text snaps back to the picked location whenever value or the list
 * changes. This id + display-text split is the pattern for replacing any
 * id-valued <select> (see GOTCHAS.md; generic form: SelectPicker).
 *
 *   allLabel   – adds a pickable empty entry for list filters ("All locations")
 *   emptyLabel – placeholder for the empty value; clearing the field picks ""
 *                (omit both for a required pick that can't be cleared)
 *   fullWidth  – fill the form column instead of the 340px picker width
 */
function LocationPicker({
    locations = [],
    value,
    onChange,
    ariaLabel = "Location",
    placeholder = "Choose a location",
    allLabel,
    emptyLabel,
    fullWidth,
    getLabel = locationLabel,
    disabled,
    required,
    id,
    style,
}) {
    const options = useMemo(() => {
        const rows = locations.map((l) => ({
            value: String(l.location_id),
            label: getLabel(l),
            sub: l.location_type ? String(l.location_type).replace(/_/g, " ").toLowerCase() : null,
        }));
        if (allLabel) return [{ value: "", label: allLabel }, ...rows];
        if (emptyLabel) return [{ value: "", label: emptyLabel }, ...rows];
        return rows;
    }, [locations, allLabel, emptyLabel, getLabel]);

    return (
        <SelectPicker
            id={id}
            options={options}
            value={value ?? ""}
            onChange={(e) => onChange?.(e.target.value)}
            aria-label={ariaLabel}
            placeholder={allLabel ? undefined : emptyLabel || placeholder}
            searchPlaceholder="Search locations…"
            disabled={disabled}
            required={required}
            style={{ width: fullWidth ? "100%" : "min(100%, 340px)", maxWidth: "calc(100vw - 32px)", ...style }}
        />
    );
}

export default LocationPicker;
